import React, { useState, useEffect } from 'react';
import { RefreshCw, ShoppingBag, Settings, Paintbrush } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';

import { getDefaultStudioUrl } from '@/components/builder/node-editor-registry';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/lib/api/trpc';

import { MountPointManager } from './components/mount-point-manager';
import { CreateMountDialog } from './components/create-mount-dialog';
import { MigrateMountDialog } from './components/migrate-mount-dialog';
import { PluginMarketplaceV2 } from './components/PluginMarketplaceV2';

/**
 * Plugins Management - Store-specific Mount Points View
 * Manage routes and switch between versions/configs
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(StorePluginsPage, PERMISSIONS.STORES_VIEW);

function StorePluginsPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'mount-points' | 'marketplace'>('marketplace'); // Default to marketplace for onboarding
  const utils = trpc.useUtils();

  // Fetch mount points for migration dialog
  const { data } = trpc.plugins.listMountPoints.useQuery(
    { storeId: id! },
    { enabled: !!id && activeTab === 'mount-points' },
  );

  // Auto-switch to marketplace if install query param exists
  useEffect(() => {
    const installPluginId = searchParams.get('install');
    if (installPluginId) {
      setActiveTab('marketplace');
    }
  }, [searchParams]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await utils.plugins.listMountPoints.invalidate();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!id) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-muted-foreground">No store selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Plugins</h1>
          <p className="text-muted-foreground text-sm">
            {activeTab === 'marketplace'
              ? 'Browse and install plugins for your store'
              : 'Manage routes and switch between versions/configs'}
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'marketplace' && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                // Studio has no /new route: it binds a store only when the URL
                // describes an operation (mode=create here). Without that the
                // editor opens unbound — the template stash falls back to its
                // standalone key and Publish pre-selects the first store of the
                // account instead of this one (#TAG-2456).
                const url = new URL('/edit', getDefaultStudioUrl());
                url.searchParams.set('mode', 'create');
                url.searchParams.set('storeId', id);
                url.searchParams.set('stepType', 'landing');
                // Suffixed like the funnel editor does: the name seeds the plugin
                // id, so two pages created here must not collide.
                url.searchParams.set('suggestedName', `New Page ${Math.random().toString(36).substring(2, 6)}`);
                window.open(url.toString(), '_blank');
              }}
            >
              <Paintbrush className="h-4 w-4" />
              Create New Page
            </Button>
          )}
          {activeTab === 'mount-points' && (
            <>
              <CreateMountDialog id={id} onSwitchToMarketplace={() => setActiveTab('marketplace')} />
              <MigrateMountDialog id={id} mountPoints={data?.mountPoints || []} />
            </>
          )}
          <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="mount-points" className="gap-2">
            <Settings className="h-4 w-4" />
            Mount Points
          </TabsTrigger>
          <TabsTrigger value="marketplace" className="gap-2">
            <ShoppingBag className="h-4 w-4" />
            Marketplace
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mount-points" className="mt-4">
          <MountPointManager id={id} />
        </TabsContent>

        <TabsContent value="marketplace" className="mt-4">
          <PluginMarketplaceV2
            storeId={id}
            autoInstallPluginId={searchParams.get('install') || undefined}
            autoInstallDeploymentId={searchParams.get('deployment') || undefined}
            autoInstallPreset={searchParams.get('preset') || undefined}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
